import { useState, useEffect } from "react";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import Card from "../../components/Card";
import { AppUser } from "../../types/user";
import { 
  UsersIcon, 
  BookOpenIcon, 
  ChatBubbleIcon,
  DocumentTextIcon,
  CogIcon
} from "../../components/icons/OutlineIcons";

interface Course {
  id: string;
  title: string;
  instructorId: string;
}

export default function ModeratorDashboard() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [instructor, setInstructor] = useState<AppUser | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadModeratorData() {
      if (!user) return;

      const userData = user as any;
      
      // Check if user is moderator
      if (userData.role !== "moderator") {
        navigate("/");
        return;
      }

      try {
        // Load instructor data
        if (userData.instructorId) {
          const instructorDoc = await getDoc(doc(db, "users", userData.instructorId));
          if (instructorDoc.exists()) {
            setInstructor({ uid: instructorDoc.id, ...instructorDoc.data() } as AppUser);

            // Load instructor's courses
            const coursesQuery = query(
              collection(db, "courses"),
              where("instructorId", "==", userData.instructorId)
            );
            const coursesSnap = await getDocs(coursesQuery);
            const coursesData = coursesSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })) as Course[];
            setCourses(coursesData);
          }
        }
      } catch (error) {
        console.error("Error loading moderator data:", error);
      } finally {
        setLoading(false);
      }
    }

    loadModeratorData();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft">
      {/* Header */}
      <div className="bg-gradient-primary text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheckIcon className="w-10 h-10" />
            <h1 className="text-4xl font-bold">Moderator Dashboard</h1>
          </div>
          <p className="text-white/90">
            จัดการและดูแลระบบให้กับ {instructor?.name || "ผู้สอน"}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                <BookOpenIcon className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-textLight">คอร์สทั้งหมด</p>
                <p className="text-3xl font-bold text-textDark">{courses.length}</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                <UsersIcon className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-textLight">นักเรียนทั้งหมด</p>
                <p className="text-3xl font-bold text-textDark">0</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                <ChatBubbleIcon className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-textLight">ข้อความใหม่</p>
                <p className="text-3xl font-bold text-textDark">0</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Link to="/moderator/courses">
            <Card className="hover:shadow-xl transition-all cursor-pointer group">
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <BookOpenIcon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg text-textDark mb-2">
                  จัดการคอร์ส
                </h3>
                <p className="text-sm text-textLight">
                  เพิ่ม/แก้ไขคอร์สและเนื้อหา
                </p>
              </div>
            </Card>
          </Link>

          <Link to="/moderator/students">
            <Card className="hover:shadow-xl transition-all cursor-pointer group">
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <UsersIcon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg text-textDark mb-2">
                  จัดการนักเรียน
                </h3>
                <p className="text-sm text-textLight">
                  ดูรายชื่อและความคืบหน้า
                </p>
              </div>
            </Card>
          </Link>

          <Link to="/moderator/support">
            <Card className="hover:shadow-xl transition-all cursor-pointer group">
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <ChatBubbleIcon className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-bold text-lg text-textDark mb-2">
                  ตอบคำถาม
                </h3>
                <p className="text-sm text-textLight">
                  ให้คำปรึกษานักเรียน
                </p>
              </div>
            </Card>
          </Link>

          <Link to="/moderator/settings">
            <Card className="hover:shadow-xl transition-all cursor-pointer group">
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <CogIcon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg text-textDark mb-2">
                  ตั้งค่าระบบ
                </h3>
                <p className="text-sm text-textLight">
                  ปรับแต่งหน้าเว็บและระบบ
                </p>
              </div>
            </Card>
          </Link>

          <Link to="/moderator/content">
            <Card className="hover:shadow-xl transition-all cursor-pointer group">
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <DocumentTextIcon className="w-8 h-8 text-pink-600" />
                </div>
                <h3 className="font-bold text-lg text-textDark mb-2">
                  เพิ่มเนื้อหา
                </h3>
                <p className="text-sm text-textLight">
                  เพิ่มคลิปและรายละเอียด
                </p>
              </div>
            </Card>
          </Link>
        </div>

        {/* Instructor Info */}
        {instructor && (
          <Card>
            <h2 className="text-2xl font-bold text-textDark mb-4">
              ข้อมูลผู้สอน
            </h2>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {instructor.name?.[0] || instructor.email[0].toUpperCase()}
              </div>
              <div>
                <p className="font-bold text-lg text-textDark">{instructor.name || "ผู้สอน"}</p>
                <p className="text-sm text-textLight">{instructor.email}</p>
                <p className="text-xs text-textLight mt-1">
                  แพ็คเกจ: <span className="font-semibold">{instructor.subscriptionTier?.toUpperCase() || "FREE"}</span>
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// Shield Check Icon
const ShieldCheckIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);
